import type {
	FullCourse,
	FullCourseOffering,
	FullCourseOfferings,
} from "@repo/sanity-utils/query-types";
import {
	getKeyedCopy,
	getKeyedCopyText,
	type KeyedCopyEntry,
} from "@repo/utils/keyedCopy";
import {isOfferingAvailableAtCampus} from "@/lib/activeCourseOffering";
import type {DiscoveryEventKey} from "@/app/actions/trackDiscoveryEventUtils";
import {CTA_COPY} from "../CTACopyConstants";
import {appendCampusQueryParameter, appendScheduleAnchor} from "../courseUrl";

export interface SecondaryCTAScheduleLink {
	href: string;
	label: string;
	description: string;
	slug: string;
	/** The offering's own identifier, distinct from `slug` (which may be its track). */
	offeringKey?: string;
	/** Set when the offering belongs to a track (e.g. Scholars, Honors). */
	trackSlug?: string;
}

const PLACEMENT_TEST_MODALITIES = new Set([
	"video-based",
	"chat-based",
	"self-paced",
]);

/**
 * Header text for the secondary CTA
 */
export function getSecondaryCTAHeader(
	scheduleLinks: SecondaryCTAScheduleLink[],
	keyedCopy: KeyedCopyEntry[] = []
) {
	const slugs = new Set(scheduleLinks.map((link) => link.slug));
	const isConsultationAndPlacement =
		scheduleLinks.length === 2 &&
		slugs.has("book-consultation") &&
		slugs.has("placement-test");

	// Consultation + placement is not a multi-track schedule choice.
	if (isConsultationAndPlacement) {
		return (
			getKeyedCopyText(
				keyedCopy,
				CTA_COPY.secondary.header.bookConsultation.key
			) || CTA_COPY.secondary.header.bookConsultation.fallback
		);
	}

	// When multiple tracks are available
	if (scheduleLinks.length > 1) {
		return (
			getKeyedCopyText(keyedCopy, CTA_COPY.secondary.header.multiTrack.key) ||
			CTA_COPY.secondary.header.multiTrack.fallback
		);
	}

	// If the only link is book consultation, return a special header
	const [onlyLink] = scheduleLinks;
	if (onlyLink?.slug === "placement-test") {
		return (
			getKeyedCopyText(
				keyedCopy,
				CTA_COPY.secondary.header.placementTest.key
			) || CTA_COPY.secondary.header.placementTest.fallback
		);
	} else if (onlyLink?.slug === "book-consultation") {
		return (
			getKeyedCopyText(
				keyedCopy,
				CTA_COPY.secondary.header.bookConsultation.key
			) || CTA_COPY.secondary.header.bookConsultation.fallback
		);
	}

	// Otherwise, return the description of the track
	if (!onlyLink) {
		return "";
	}

	const headerTemplate =
		getKeyedCopyText(keyedCopy, CTA_COPY.secondary.header.oneTrack.key) ||
		CTA_COPY.secondary.header.oneTrack.fallback;
	return headerTemplate.replace("{{trackName}}", onlyLink.description);
}

/**
 * Get the data for a course schedule link
 */
function buildScheduleLink({
	candidate,
	campusIdForUrl,
	keyedCopy,
	slug,
	sequenceType,
	preferNoTrackLabel = false,
}: {
	candidate: FullCourseOffering;
	campusIdForUrl: string | null;
	keyedCopy: KeyedCopyEntry[];
	slug?: string;
	sequenceType?: NonNullable<FullCourse>["sequenceType"];
	/** When true, use the no-track button label even if the offering has a track. */
	preferNoTrackLabel?: boolean;
}): SecondaryCTAScheduleLink {
	const hasTrack =
		candidate.track && candidate.track.slug.current !== "track-none";
	const linkLabel = hasTrack ? candidate.track.title : candidate.title;
	const linkSlug =
		slug ?? (hasTrack ? candidate.track.slug.current : candidate._key);
	const trackSlug = hasTrack ? candidate.track.slug.current : undefined;

	let label: string;
	let description: string;

	// Self-paced uses its own label for a single offering; multi-track uses track labels.
	if (candidate.modality.slug.current === "self-paced" && preferNoTrackLabel) {
		label = getKeyedCopy(keyedCopy, CTA_COPY.button.selfPaced);
		description = candidate.title;
	} else if (sequenceType === "versions") {
		// Special case for versions sequence type
		const versionsLabelTemplate =
			getKeyedCopyText(keyedCopy, CTA_COPY.button.versions.key) ||
			CTA_COPY.button.versions.fallback;
		label = versionsLabelTemplate.replace(
			"{{versionName}}",
			candidate.sequenceLabel ?? ""
		);
		description = candidate.sequenceLabel ?? linkLabel;
	} else {
		// Otherwise this is based on track
		const trackedLabelTemplate =
			getKeyedCopyText(keyedCopy, CTA_COPY.button.track.key) ||
			CTA_COPY.button.track.fallback;
		const untrackedLabel =
			getKeyedCopyText(keyedCopy, CTA_COPY.button.noTrack.key) ||
			CTA_COPY.button.noTrack.fallback;
		label =
			hasTrack && !preferNoTrackLabel
				? trackedLabelTemplate.replace("{{trackName}}", linkLabel)
				: untrackedLabel;
		description = linkLabel;
	}

	let href = appendCampusQueryParameter(candidate.courseUrl, campusIdForUrl);
	// Versions sequence links stay on the version page without a schedule anchor.
	if (sequenceType !== "versions") {
		href = appendScheduleAnchor(href, candidate.modality.slug.current);
	}

	return {
		href,
		label,
		description,
		slug: linkSlug,
		offeringKey: candidate._key,
		trackSlug,
	};
}

/**
 * Builds one schedule link per course track for the active offering.
 *
 * Offerings are limited to the same modality and sequence part as the active
 * offering so, for example, selecting Part 1 only links to Part 1 schedules.
 * The active offering is included. A Map keyed by track ID removes duplicate
 * offerings for the same track while preserving the source order. When
 * duplicates exist, the last one supplies the schedule URL.
 *
 * Offerings that list campuses are also dropped when the visitor's selected
 * campus is not one of them, so a track such as Scholars is not linked at a
 * campus that does not teach it.
 *
 * Versions sequence types skip filtering and return a link for every offering.
 *
 * The selected campus is appended to each URL when one is available.
 */
export function getCourseOfferingScheduleLinks({
	courseOfferings,
	offering,
	campusIdForUrl,
	selectedCampusId = null,
	keyedCopy = [],
	sequenceType,
}: {
	courseOfferings: FullCourseOfferings;
	offering: FullCourseOffering;
	campusIdForUrl: string | null;
	selectedCampusId?: string | null;
	keyedCopy?: KeyedCopyEntry[];
	sequenceType?: NonNullable<FullCourse>["sequenceType"];
}): SecondaryCTAScheduleLink[] {
	// Special case for versions sequence type
	if (sequenceType === "versions") {
		return courseOfferings.map((candidate) =>
			buildScheduleLink({
				candidate,
				campusIdForUrl,
				keyedCopy,
				slug: candidate._key,
				sequenceType,
			})
		);
	}

	// Otherwise match track and modality
	const offeringsByTrack = new Map<string, FullCourseOffering>();
	for (const candidate of courseOfferings) {
		// Only include offerings that match the active selection.
		const matchesActiveSelection =
			candidate.modality._id === offering.modality._id &&
			candidate.sequenceLabel === offering.sequenceLabel;

		// Skip offerings that don't match the active selection.
		if (!matchesActiveSelection) {
			continue;
		}

		// Skip offerings that aren't taught at the selected campus.
		if (!isOfferingAvailableAtCampus(candidate, selectedCampusId)) {
			continue;
		}

		const hasTrack =
			candidate.track && candidate.track.slug.current !== "track-none";
		const trackKey = hasTrack ? candidate.track._id : candidate._key;

		// Deduplicate tracked offerings by track; trackless offerings use their key.
		offeringsByTrack.set(trackKey, candidate);
	}

	// Now we have the offerings in an array, we use that to build the links
	const uniqueOfferings = Array.from(offeringsByTrack.values());
	const preferNoTrackLabel = uniqueOfferings.length === 1;

	return uniqueOfferings.map((candidate) =>
		buildScheduleLink({
			candidate,
			campusIdForUrl,
			keyedCopy,
			preferNoTrackLabel,
			sequenceType,
		})
	);
}

/**
 * Builds secondary CTA links.
 *
 * In-person keeps schedule links for matching offerings that are taught at the
 * selected campus, including when the course uses a versions sequence. Online
 * modalities show placement-test, and video-based also shows book-consultation
 * when that toggle is on. Non-in-person versions never show secondary links.
 */
export function getSecondaryCTAScheduleLinks({
	courseOfferings,
	offering,
	campusIdForUrl,
	selectedCampusId = null,
	showBookConsultationButtons = false,
	consultationUrl = "",
	hasPlacementTest = false,
	placementTestUrl = "",
	keyedCopy = [],
	sequenceType,
}: {
	courseOfferings: FullCourseOfferings;
	offering: FullCourseOffering;
	campusIdForUrl: string | null;
	selectedCampusId?: string | null;
	showBookConsultationButtons?: boolean;
	consultationUrl?: string;
	hasPlacementTest?: boolean;
	placementTestUrl?: string;
	keyedCopy?: KeyedCopyEntry[];
	sequenceType?: NonNullable<FullCourse>["sequenceType"];
}): SecondaryCTAScheduleLink[] {
	const modalitySlug = offering.modality.slug.current;

	// In-person uses schedule links.
	if (modalitySlug === "in-person") {
		return getCourseOfferingScheduleLinks({
			courseOfferings,
			offering,
			campusIdForUrl,
			selectedCampusId,
			keyedCopy,
		});
	}

	// Versions has no secondary links.
	if (sequenceType === "versions") {
		return [];
	}

	// Others show placement test and/or book consultation buttons.
	const links: SecondaryCTAScheduleLink[] = [];
	const shouldAddPlacementTest =
		hasPlacementTest && PLACEMENT_TEST_MODALITIES.has(modalitySlug);

	// Video-based can show book consultation alongside placement test.
	if (showBookConsultationButtons && modalitySlug === "video-based") {
		links.push({
			href: consultationUrl,
			label:
				getKeyedCopyText(keyedCopy, CTA_COPY.button.inPerson.key) ||
				CTA_COPY.button.inPerson.fallback,
			description: offering.track.title,
			slug: "book-consultation",
		});
	}

	if (shouldAddPlacementTest) {
		links.push({
			href: placementTestUrl,
			label:
				getKeyedCopyText(keyedCopy, CTA_COPY.button.placementTest.key) ||
				CTA_COPY.button.placementTest.fallback,
			description: offering.track?.title ?? offering.title,
			slug: "placement-test",
		});
	}

	return links;
}

export type ScheduleLinkTrackingEvent = {
	event: DiscoveryEventKey;
	properties: Record<string, unknown>;
};

/**
 * Selected offering fields shared by CTA conversion events so Mixpanel/GTM
 * can slice `course_schedule_requested` / `consultation_request_initiated`
 * by the same `modality` / `sequence` keys as `sequence_selected` and
 * `course_modality_selected`. Sequence is omitted when the offering has none
 * (Beast Academy, single-part courses). `modalitySlug` covers offering-less
 * CTAs such as Beast Academy.
 */
export function getSelectedOfferingTrackingProperties(
	offering: FullCourseOffering | null | undefined,
	modalitySlug?: string | null
): Record<string, string> {
	const properties: Record<string, string> = {};
	const modality = offering?.modality.slug.current ?? modalitySlug ?? undefined;
	if (modality) {
		properties.modality = modality;
	}
	if (offering?.sequenceLabel) {
		properties.sequence = offering.sequenceLabel;
	}
	return properties;
}

/**
 * Resolves which Discovery funnel event (if any) a schedule-link click should
 * fire: "book-consultation" is a consultation exit, "placement-test" is
 * out of scope here (tracked inside apps/placement itself), everything else
 * is a plain schedule-viewing exit.
 *
 * `campusId` is attached when a campus is actually selected — except on the
 * "book-consultation" exit, which (from the secondary CTA) only ever fires
 * for a `video-based` offering: a virtual CTA has no campus concept, so a
 * `selectedCampusId` left over from browsing an in-person offering earlier
 * in the session must not leak onto it (WEL-1815).
 */
export function resolveScheduleLinkTrackingEvent(
	slug: string,
	courseSlug: string,
	offering: FullCourseOffering | null = null,
	modalitySlug?: string | null,
	campusId?: string | null,
	offeringKey?: string | null,
	trackSlug?: string | null
): ScheduleLinkTrackingEvent | null {
	const selectedOfferingProperties = getSelectedOfferingTrackingProperties(
		offering,
		modalitySlug
	);
	const campusProperties = campusId ? {campus: campusId} : {};

	if (slug === "book-consultation") {
		// Resolved the same way as `selectedOfferingProperties.modality`
		// (offering modality wins over the raw `modalitySlug` param) so this
		// gate agrees with what the event actually reports as its modality.
		const consultationCampusProperties =
			selectedOfferingProperties.modality === "in-person" && campusId
				? {campus: campusId}
				: {};
		return {
			event: "consultation_request_initiated",
			properties: {
				course_slug: courseSlug,
				cta_location: "secondary_cta",
				...selectedOfferingProperties,
				...consultationCampusProperties,
			},
		};
	}
	if (slug === "placement-test") {
		return null;
	}
	return {
		event: "course_schedule_requested",
		properties: {
			course_slug: courseSlug,
			offering_slug: offeringKey ?? slug,
			...(trackSlug ? {track_slug: trackSlug} : {}),
			...selectedOfferingProperties,
			...campusProperties,
		},
	};
}
